// Traduction des échecs d'appel d'Edge Function en messages affichables.
//
// Un `functions.invoke` qui échoue ne rend JAMAIS un texte présentable : deux
// couches de généricité s'empilent.
//
// 1. La bibliothèque Supabase. Dès que le statut n'est pas 2xx, `error.message`
//    vaut TOUJOURS « Edge Function returned a non-2xx status code » — anglais,
//    sans cause ni geste. C'est ce texte que l'usagère lisait après
//    « Transcrire et analyser », et que la QA fonctionnelle cherchait à
//    provoquer sans y arriver.
// 2. Le corps de la réponse, où vit le VRAI motif. Mais il est écrit pour le
//    journal, pas pour l'usagère : nos edges y mettent « capture sans
//    transcript_raw », « GEMINI_API_KEY non configurée », « lecture capture:
//    JWT expired », ou la `err.message` brute du fournisseur LLM (anglaise).
//    Extraire le motif ne suffit donc pas — c'est le piège de ce module.
//
// D'où la règle du DS §6.13 appliquée ICI, au seul endroit que traversent tous
// les appels d'edge : le motif technique part au journal, l'écran reçoit une de
// NOS phrases. Le point de passage compte — `triggerTranscription` est appelée
// depuis trois écrans, dont un qu'il ne servirait à rien de patcher trois fois.

export interface InvokeLikeError {
  message: string;
  context?: unknown;
}

/**
 * Motif technique le plus précis disponible, **pour le journal uniquement** —
 * jamais pour l'affichage. On préfère `body.message` (détail authz de nos
 * edges : `{ error: "forbidden", message: "…" }`) puis `body.error` (motif des
 * réponses 422), et on retombe sur `error.message` quand le corps est absent
 * ou illisible (échec réseau, réponse HTML d'un proxy).
 *
 * `clone()` : le corps reste lisible par l'appelant après extraction.
 */
export async function invokeErrorDetail(error: InvokeLikeError): Promise<string> {
  const ctx = error.context;
  if (ctx instanceof Response) {
    try {
      const body = (await ctx.clone().json()) as { error?: unknown; message?: unknown };
      if (typeof body.message === "string" && body.message) return body.message;
      if (typeof body.error === "string" && body.error) return body.error;
    } catch {
      // corps non-JSON → repli sur le message générique de la bibliothèque
    }
  }
  return error.message;
}

// Le kill-switch budgétaire (`_shared/budget.ts`, motif « budget suspendu —
// … » écrit par `_shared/transcribe.ts`) est le SEUL refus dont le geste
// diffère : réessayer ne servira à rien avant le mois prochain, dire
// « réessaie » serait un faux conseil (DS §6.13, exigence 3 : le geste).
//
// C'est une reconnaissance par cas, ce que le DS met en garde — mais le défaut
// de `friendlyNetworkError` est de RETOMBER SUR LE TEXTE BRUT quand le motif
// n'est pas reconnu. Ici le repli est une phrase à nous : l'étanchéité ne
// dépend pas de la liste des cas.
const BUDGET_SUSPENDED_PATTERN = /budget suspendu/i;

// Reprend la formulation validée de `BudgetSuspendedBanner` (fiche de clôture
// 21-07) et y ajoute le geste, que la bannière n'a pas à porter.
export const BUDGET_SUSPENDED_MESSAGE =
  "Traitements IA suspendus — le budget du mois est atteint. Inutile de réessayer : écris-nous si c'est urgent.";

/**
 * Journalise un motif technique DÉJÀ extrait et rend la phrase à afficher.
 * Pour les edges qui répondent 200 avec un `{ error }` dans le corps (le
 * `invoke` ne signale alors aucune erreur).
 *
 * @param scope   d'où vient l'appel — préfixe de journal, jamais affiché.
 * @param detail  motif technique du serveur.
 * @param generic notre phrase, propre à l'action tentée.
 */
export function edgeErrorMessage(scope: string, detail: string, generic: string): string {
  logEdgeError(scope, detail);
  return BUDGET_SUSPENDED_PATTERN.test(detail) ? BUDGET_SUSPENDED_MESSAGE : generic;
}

/**
 * Journalise seulement — pour les appels dont l'écran n'affiche déjà pas le
 * motif (il a sa propre phrase, ou n'utilise que le booléen). Sans ça, un
 * échec de `process-feedback` ne laissait aucune trace nulle part.
 */
export function logEdgeError(scope: string, detail: string): void {
  console.error(`[${scope}] appel d'edge échoué :`, detail);
}

/**
 * Journalise le motif technique d'un échec de `functions.invoke` et rend la
 * phrase à afficher. C'est la seule fonction que les libs d'appel d'edge
 * doivent utiliser pour un message destiné à l'écran.
 */
export async function invokeErrorMessage(
  scope: string,
  error: InvokeLikeError,
  generic: string,
): Promise<string> {
  return edgeErrorMessage(scope, await invokeErrorDetail(error), generic);
}
